import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import usePetViewOwnerId from "../hooks/usePetViewOwnerId";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { DELETE_PET } from "../../../enums/PermissionTitles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PETS } from "../../../enums/QueryKeys";
import { deletePet } from "../../../enums/queryFunction/Delete";
import useConfirm from "../../../hooks/useConfirm";
import { SnackbarContext } from "../../../contexts/Snackbar";
import * as urlPaths from "../../../enums/UrlPaths";
import useUser from "../../../hooks/useUser";
import { Delete } from "@mui/icons-material";

export default function DeletePet() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const ownerId = usePetViewOwnerId();
  const { roles, profile } = useUser();
  const navigate = useNavigate();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { t } = useTranslation();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("AreYouSure"),
    t("Accept"),
    t("Cancel"),
    "warning"
  );

  async function handleDeletePet(e) {
    e.preventDefault();
    getMessage(t("DeletePetConfirmation"));
    const ans = await confirm();
    if (ans) {
      mutate(id);
    }
  }

  function removeFromQuery(key) {
    queryClient.setQueryData([key], (oldData) => {
      if (!oldData) return oldData;
      const updatedData = oldData.filter(
        (item) => Number(item.id) !== Number(id)
      );
      return updatedData;
    });
  }

  const { mutate } = useMutation({
    mutationFn: deletePet,
    onSuccess: () => {
      const keys = [PETS, PETS + ownerId];
      keys.forEach((key) => removeFromQuery(key));
      handleSnackbar("successMessage", "success");
      queryClient.invalidateQueries([PETS + "user" + ownerId]);
      //If the owner is viewing their pet
      if (Number(profile.id) == Number(ownerId)) {
        navigate(urlPaths.PETS);
      } else if (roles.adminOrEmployee || roles.master) {
        if (ownerId) {
          navigate(urlPaths.SINGLE_CLINIC_USER + ownerId);
        } else {
          navigate(-1);
        }
      } else {
        navigate(urlPaths.PETS);
      }
    },
    onError: (error) => {
      handleSnackbar("errorMessage", "error");
    },
  });

  if (isPermissionAllowed(DELETE_PET)) {
    return (
      <form onSubmit={(e) => handleDeletePet(e)}>
        <Button type="submit" color="error" variant="contained" startIcon={<Delete />} data-testid="pet.management.delete-pet.button">
          {t("DeletePet")}
        </Button>
        <ConfirmationDialog />
      </form>
    );
  }
}
